import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  DevicePhoneMobileIcon, ShieldCheckIcon, BanknotesIcon,
  UserIcon, ChatBubbleLeftIcon, DocumentTextIcon, ArrowPathIcon, CheckCircleIcon, XCircleIcon,
  PlusIcon, BuildingOffice2Icon, ExclamationTriangleIcon, ArrowUpTrayIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import BankDirectoryEditor from '../../../components/payout/BankDirectoryEditor'
import KycDocumentsEditor from '../../../components/payout/KycDocumentsEditor'
import UarCatalogEditor from '../../../components/mobile/UarCatalogEditor'

/** Map API camelCase keys to venue_configuration.setting_key (snake_case). */
const PAYOUT_SETTING_CAMEL_TO_SNAKE = {
  userCaptureLivenessEnabled: 'user_capture_liveness',
  userCaptureSelfieEnabled: 'user_capture_selfie',
  statement1Title: 'statement_1_title',
  statement1Paragraphs: 'statement_1_paragraphs',
  statement2Title: 'statement_2_title',
  statement2Paragraphs: 'statement_2_paragraphs',
}

function payoutSettingKeyToSnake(camel) {
  if (PAYOUT_SETTING_CAMEL_TO_SNAKE[camel]) return PAYOUT_SETTING_CAMEL_TO_SNAKE[camel]
  return camel.replace(/([A-Z])/g, '_$1').toLowerCase()
}

function payoutSettingValueType(key, value) {
  if (typeof value === 'boolean') return 'boolean'
  if (Array.isArray(value) || (value && typeof value === 'object')) return 'json'
  if (key === 'kycConfidenceThreshold' || key === 'rekycIntervalMonths' || key === 'cashLimit') return 'number'
  return 'string'
}

const SECTIONS = [
  { id: 'kyc', label: 'KYC & Verification', icon: ShieldCheckIcon },
  { id: 'payment', label: 'Payment Options', icon: BanknotesIcon },
  { id: 'banks', label: 'Banks', icon: BuildingOffice2Icon },
  { id: 'patron', label: 'Patron & Fields', icon: UserIcon },
  { id: 'uar', label: 'UAR', icon: ExclamationTriangleIcon },
  { id: 'statements', label: 'Statements & Signatures', icon: DocumentTextIcon },
  { id: 'comments', label: 'Comments & Notes', icon: ChatBubbleLeftIcon },
]

function paragraphsToText(paragraphs) {
  if (!Array.isArray(paragraphs)) return ''
  return paragraphs.join('\n\n')
}

function textToParagraphs(text) {
  return String(text || '')
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex-1 mr-4">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {description && <div className="text-xs text-gray-500 mt-0.5">{description}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${checked ? 'bg-teal-600' : 'bg-gray-300'}`}
      >
        <span
          className="inline-block h-3.5 w-3.5 transform rounded-full bg-white transition"
          style={{ transform: checked ? 'translateX(18px)' : 'translateX(2px)' }}
        />
      </button>
    </div>
  )
}

function ParagraphContentEditor({ value, onChange, label, description, rows = 6, placeholder }) {
  const text = paragraphsToText(value)

  return (
    <div className="py-2">
      <label className="text-sm font-medium text-gray-900">{label}</label>
      {description && <div className="text-xs text-gray-500 mt-0.5 mb-2">{description}</div>}
      <textarea
        rows={rows}
        className="w-full text-sm border border-gray-300 rounded p-2"
        spellCheck
        placeholder={placeholder}
        value={text}
        onChange={(e) => onChange(textToParagraphs(e.target.value))}
      />
    </div>
  )
}

function TagListEditor({ value, onChange, label, description, allowBulkImport = false }) {
  const tags = Array.isArray(value) ? value : []
  const [newTag, setNewTag] = useState('')
  const [showImportModal, setShowImportModal] = useState(false)
  const [importPreview, setImportPreview] = useState([])
  const [importMode, setImportMode] = useState('merge')
  const fileInputRef = useRef(null)

  const addTag = () => {
    const t = newTag.trim()
    if (t && !tags.includes(t)) {
      onChange([...tags, t])
      setNewTag('')
    }
  }

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target.result
      const items = parseImportFile(file.name, text)
      setImportPreview(items)
      setShowImportModal(true)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  const parseImportFile = (filename, text) => {
    const lowerName = filename.toLowerCase()
    const items = []

    if (lowerName.endsWith('.csv') || lowerName.endsWith('.txt')) {
      const lines = text.split(/\r?\n/)
      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed && !trimmed.startsWith('#')) {
          const firstCol = trimmed.split(',')[0].trim().replace(/^["']|["']$/g, '')
          if (firstCol) items.push(firstCol)
        }
      }
    } else {
      const lines = text.split(/\r?\n/)
      for (const line of lines) {
        const trimmed = line.trim()
        if (trimmed) items.push(trimmed)
      }
    }

    return [...new Set(items)]
  }

  const confirmImport = () => {
    if (importMode === 'replace') {
      onChange(importPreview)
    } else {
      const merged = [...new Set([...tags, ...importPreview])]
      onChange(merged)
    }
    setShowImportModal(false)
    setImportPreview([])
  }

  const exportToCSV = () => {
    const csv = tags.join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${label.replace(/\s+/g, '_').toLowerCase()}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="py-2">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-medium text-gray-900">{label}</div>
        {allowBulkImport && (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded hover:bg-blue-100 flex items-center gap-1"
              title="Import from CSV/Excel"
            >
              <ArrowUpTrayIcon className="w-3.5 h-3.5" />
              Import
            </button>
            {tags.length > 0 && (
              <button
                type="button"
                onClick={exportToCSV}
                className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200 flex items-center gap-1"
                title="Export to CSV"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                Export
              </button>
            )}
          </div>
        )}
      </div>
      {description && <div className="text-xs text-gray-500 mt-0.5 mb-2">{description}</div>}
      
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.txt,.xlsx,.xls"
        onChange={handleFileUpload}
        className="hidden"
      />

      <div className="flex flex-wrap gap-1.5 mb-2">
        {tags.map((tag, i) => (
          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
            {tag}
            <button type="button" onClick={() => onChange(tags.filter((_, j) => j !== i))} className="ml-1 text-gray-400 hover:text-red-500">
              <XCircleIcon className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-1">
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
          placeholder="Add new..."
          className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
        />
        <button type="button" onClick={addTag} className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200">
          <PlusIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Import Preview</h3>
              <p className="text-sm text-gray-500 mt-1">
                Found {importPreview.length} items. Choose how to import:
              </p>
            </div>

            <div className="px-6 py-4 border-b bg-gray-50">
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="importMode"
                    value="merge"
                    checked={importMode === 'merge'}
                    onChange={(e) => setImportMode(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">
                    <strong>Merge</strong> - Add new items, keep existing ({tags.length} current + {importPreview.filter(item => !tags.includes(item)).length} new = {[...new Set([...tags, ...importPreview])].length} total)
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="importMode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={(e) => setImportMode(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">
                    <strong>Replace</strong> - Remove all existing, use only imported ({importPreview.length} items)
                  </span>
                </label>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="flex flex-wrap gap-1.5">
                {importPreview.map((item, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                      tags.includes(item) ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {item}
                    {tags.includes(item) && <span className="ml-1 text-yellow-600">(exists)</span>}
                  </span>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowImportModal(false)
                  setImportPreview([])
                }}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmImport}
                className="px-4 py-2 text-sm bg-teal-700 text-white rounded hover:bg-teal-800"
              >
                Import {importPreview.length} Items
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** Platform Mobile tab: tenant-wide payout/KYC settings (group scope). Tenant is chosen on Live tenants. */
export default function TenantMobileConfigTab({ groupId }) {
  const apiBase = useMemo(
    () => getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}/payout-mobile`),
    [groupId]
  )

  const [settings, setSettings] = useState({})
  const [uarCatalog, setUarCatalog] = useState([])
  const [nilOnePerDay, setNilOnePerDay] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savingUar, setSavingUar] = useState(false)
  const [message, setMessage] = useState(null)
  const [activeSection, setActiveSection] = useState('kyc')

  const fetchSettings = useCallback(async () => {
    if (!groupId) return
    setLoading(true)
    try {
      const res = await authFetch(`${apiBase}/rules`)
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setSettings(json.data?.settings || {})
      setUarCatalog(json.data?.uarCatalog || json.data?.settings?.uarCatalog || [])
      setNilOnePerDay(json.data?.nilOnePerDay === true)
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings: ' + err.message })
    } finally {
      setLoading(false)
    }
  }, [apiBase, groupId])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const saveUarCatalog = async () => {
    setSavingUar(true)
    setMessage(null)
    try {
      const res = await authFetch(`${apiBase}/uar-catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uarCatalog,
          nilOnePerDay,
          scope: 'group',
          venue_id: null,
        }),
      })
      const json = await res.json()
      if (json.success) {
        setMessage({ type: 'success', text: 'UAR catalog saved' })
        await fetchSettings()
      } else {
        setMessage({ type: 'error', text: json.error || 'UAR save failed' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'UAR save failed: ' + err.message })
    } finally {
      setSavingUar(false)
    }
  }

  const saveSettings = async () => {
    setSaving(true)
    setMessage(null)
    try {
      const payoutCardOn = settings.payoutCardEnabled !== false
      const titoOn = settings.titoEnabled !== false
      if (!payoutCardOn && !titoOn) {
        setMessage({ type: 'error', text: 'Enable at least one of Payout card or Tito on the slip step.' })
        setSaving(false)
        return
      }
      const payload = Object.entries(settings)
        .filter(([key]) => key !== 'nswDigitalLicenceQrUrl' && key !== 'uarCatalog' && key !== 'incident_types')
        .map(([key, value]) => {
          const snakeKey = payoutSettingKeyToSnake(key)
          let val
          if (value == null) val = ''
          else if (typeof value === 'object') val = JSON.stringify(value)
          else val = String(value)
          return {
            scope: 'group',
            venue_id: null,
            category: 'payout',
            key: snakeKey,
            value: val,
            valueType: payoutSettingValueType(key, value),
          }
        })

      const res = await authFetch(`${apiBase}/venue-config/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: payload }),
      })
      const json = await res.json()
      if (json.success) {
        setMessage({ type: 'success', text: 'Payout mobile configuration saved' })
      } else {
        setMessage({ type: 'error', text: json.error || 'Save failed' })
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Save failed: ' + err.message })
    } finally {
      setSaving(false)
    }
  }

  if (!groupId) return null

  if (loading) {
    return <p className="text-sm text-gray-500 py-8">Loading mobile configuration…</p>
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <DevicePhoneMobileIcon className="w-5 h-5 text-teal-700" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Payout mobile configuration</h2>
              <p className="text-sm text-gray-500">
                Tenant-wide settings for floor tablets (all venues under this tenant). UAR catalog is under the{' '}
                <strong>UAR</strong> sub-tab here or the tenant <strong>UAR</strong> tab above.
              </p>
            </div>
          </div>
          <button type="button" onClick={fetchSettings} className="p-2 border border-gray-300 rounded-md hover:bg-gray-50">
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {message && (
        <div
          className={`rounded-lg p-3 flex items-center ${
            message.type === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
          ) : (
            <XCircleIcon className="w-5 h-5 text-red-600 mr-2" />
          )}
          <span className={`text-sm ${message.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>{message.text}</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b flex flex-wrap">
          {SECTIONS.filter((s) => s?.id && s.icon).map((s) => {
            const TabIcon = s.icon
            return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${
                activeSection === s.id
                  ? 'border-teal-600 text-teal-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TabIcon className="w-4 h-4 mr-1.5" />
              {s.label}
            </button>
            )
          })}
        </div>

        <div className="p-6">
          {activeSection === 'kyc' && (
            <div className="space-y-1 divide-y divide-gray-100">
              <Toggle
                checked={settings.selfKycEnabled}
                onChange={(v) => updateSetting('selfKycEnabled', v)}
                label="Self-service KYC"
                description="Allow patron to perform KYC themselves on the device"
              />
              <Toggle
                checked={settings.nswDigitalLicenceEnabled}
                onChange={(v) => updateSetting('nswDigitalLicenceEnabled', v)}
                label="NSW Digital Licence"
                description="Accept NSW digital licence for KYC verification"
              />
              {settings.nswDigitalLicenceEnabled && (
                <p className="text-xs text-gray-500 pl-1 pb-2 -mt-1">
                  Per-tablet NSW QR images are configured under the <strong>Devices</strong> tab (NSW DDL) for each floor
                  device, not here.
                </p>
              )}
              <Toggle
                checked={settings.showKycResultToUser}
                onChange={(v) => updateSetting('showKycResultToUser', v)}
                label="Show KYC result to patron"
                description="When off, mobile hides DVS/KYC failure details and DVS alerts; web Monitor alerts still fire"
              />
              <Toggle
                checked={settings.requirePhotoId}
                onChange={(v) => updateSetting('requirePhotoId', v)}
                label="Require photo ID"
                description="Require photo ID scan during KYC"
              />
              <Toggle
                checked={settings.showKycRefusedOption}
                onChange={(v) => updateSetting('showKycRefusedOption', v)}
                label="KYC refused option"
                description="Show option to capture when patron refuses to provide KYC"
              />
              <div className="py-3 border-t border-gray-100 mt-1">
                <div className="text-sm font-medium text-gray-900 mb-0.5">Assisted KYC — ID scan review</div>
                <p className="text-xs text-gray-500 mb-2">
                  After staff scans ID, control which patron-facing capture action appears. Enable at most one; if both are
                  on, mobile prefers liveness.
                </p>
                <Toggle
                  checked={!!settings.userCaptureLivenessEnabled}
                  onChange={(v) => updateSetting('userCaptureLivenessEnabled', v)}
                  label="Patron capture: liveness"
                  description="Show only the liveness step (hide selfie and plain Continue to KYC)"
                />
                <Toggle
                  checked={!!settings.userCaptureSelfieEnabled}
                  onChange={(v) => updateSetting('userCaptureSelfieEnabled', v)}
                  label="Patron capture: selfie"
                  description="Show only the selfie step; if both liveness and selfie are off, show Continue to KYC only"
                />
              </div>
              <div className="py-2">
                <label className="text-sm font-medium text-gray-900">KYC confidence threshold</label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="range"
                    min="0.50"
                    max="1.00"
                    step="0.05"
                    value={settings.kycConfidenceThreshold || 0.85}
                    onChange={(e) => updateSetting('kycConfidenceThreshold', parseFloat(e.target.value))}
                    className="flex-1"
                  />
                  <span className="text-sm font-mono w-12 text-right">
                    {(settings.kycConfidenceThreshold || 0.85).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-gray-500">Minimum OCR/face match confidence to auto-accept</p>
              </div>
              <div className="py-2">
                <label className="text-sm font-medium text-gray-900">Re-KYC interval (months)</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={settings.rekycIntervalMonths || 12}
                  onChange={(e) => updateSetting('rekycIntervalMonths', parseInt(e.target.value, 10))}
                  className="mt-1 w-24 px-2 py-1 text-sm border border-gray-300 rounded"
                />
                <p className="text-xs text-gray-500 mt-0.5">Months before re-KYC is required for the same patron</p>
              </div>
              <div className="py-2">
                <label className="text-sm font-medium text-gray-900">Licence image retention</label>
                <select
                  value={settings.licenceRetentionPolicy || 'keep_on_failure_only'}
                  onChange={(e) => updateSetting('licenceRetentionPolicy', e.target.value)}
                  className="mt-1 px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="always_keep">Always keep</option>
                  <option value="keep_on_failure_only">Keep on DVS failure only</option>
                  <option value="never_keep">Never keep (delete after DVS)</option>
                </select>
              </div>
              <TagListEditor
                value={settings.kycRefusalReasons}
                onChange={(v) => updateSetting('kycRefusalReasons', v)}
                label="KYC refusal reasons"
                description="Predefined reasons when patron refuses KYC"
              />
              <TagListEditor
                value={settings.kycCompletionNotes}
                onChange={(v) => updateSetting('kycCompletionNotes', v)}
                label="KYC completion notes"
                description="Predefined note tags after KYC completes (e.g., Worn out, Photo not clear)"
              />
              <KycDocumentsEditor
                value={settings.kycDocuments}
                onChange={(v) => updateSetting('kycDocuments', v)}
              />
              <Toggle
                checked={!!settings.requireCitizenshipDeclaration}
                onChange={(v) => updateSetting('requireCitizenshipDeclaration', v)}
                label="Require citizenship declaration"
                description="Ask Single or More than one citizenship, then country picker(s). Needed for AU licence / Medicare where citizenship is not on the document (including dual citizenship)."
              />
            </div>
          )}

          {activeSection === 'payment' && (
            <div className="space-y-1 divide-y divide-gray-100">
              <div className="py-2 pb-3">
                <div className="text-sm font-medium text-gray-900">Payout slip / ticket type</div>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">Shown on the mobile slip step; staff can use one or both.</p>
                <Toggle
                  checked={settings.payoutCardEnabled !== false}
                  onChange={(v) => updateSetting('payoutCardEnabled', v)}
                  label="Payout card"
                  description="Allow payout card number entry and barcode scan"
                />
                <Toggle
                  checked={settings.titoEnabled !== false}
                  onChange={(v) => updateSetting('titoEnabled', v)}
                  label="Tito (TITO ticket)"
                  description="Allow TITO ticket number entry and barcode scan"
                />
              </div>
              <Toggle
                checked={settings.showCashOption}
                onChange={(v) => updateSetting('showCashOption', v)}
                label="Show cash option"
                description="Show/hide cash payment method"
              />
              <Toggle
                checked={settings.eftEnabled}
                onChange={(v) => updateSetting('eftEnabled', v)}
                label="EFT payments"
                description="Allow EFT payout method"
              />
              <Toggle
                checked={settings.chequeEnabled}
                onChange={(v) => updateSetting('chequeEnabled', v)}
                label="Cheque payments"
                description="Allow cheque payout method"
              />
              <Toggle
                checked={settings.showOverseasBank}
                onChange={(v) => updateSetting('showOverseasBank', v)}
                label="Overseas bank option"
                description="Show overseas bank account option in payment"
              />
              <Toggle
                checked={settings.showBankVerifiedCheckbox}
                onChange={(v) => updateSetting('showBankVerifiedCheckbox', v)}
                label="Bank verified checkbox"
                description="Show checkbox to mark bank account as verified"
              />
              <Toggle
                checked={settings.cashLimitEnforced}
                onChange={(v) => updateSetting('cashLimitEnforced', v)}
                label="Enforce cash limit"
                description="Block cash above limit (vs warning only)"
              />
              <div className="py-2">
                <label className="text-sm font-medium text-gray-900">Cash limit ($)</label>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={settings.cashLimit || 5000}
                  onChange={(e) => updateSetting('cashLimit', parseInt(e.target.value, 10))}
                  className="mt-1 w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                />
                <p className="text-xs text-gray-500 mt-0.5">Maximum cash payout amount (AUS regulatory)</p>
              </div>
            </div>
          )}

          {activeSection === 'banks' && (
            <div className="space-y-1">
              <BankDirectoryEditor
                value={settings.bankDirectory}
                onChange={(v) => updateSetting('bankDirectory', v)}
              />
            </div>
          )}

          {activeSection === 'patron' && (
            <div className="space-y-1 divide-y divide-gray-100">
              <div className="py-2">
                <label className="text-sm font-medium text-gray-900">Patron ID requirement</label>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                  When set to Either, mobile requires at least one of Member ID or Visitor ID before leaving the slip step.
                </p>
                <select
                  value={settings.patronIdRequirement === 'either' ? 'either' : 'none'}
                  onChange={(e) => updateSetting('patronIdRequirement', e.target.value)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded"
                >
                  <option value="none">None — both optional</option>
                  <option value="either">Either — member or visitor required</option>
                </select>
              </div>
              <Toggle
                checked={settings.patronPhoneRequired}
                onChange={(v) => updateSetting('patronPhoneRequired', v)}
                label="Mobile phone required"
                description="Patron mobile phone number is mandatory by default"
              />
              <Toggle
                checked={settings.showPhoneRefusedOption}
                onChange={(v) => updateSetting('showPhoneRefusedOption', v)}
                label="Phone refused option"
                description="Show option when patron refuses to provide phone number"
              />
              <Toggle
                checked={settings.showOccupation}
                onChange={(v) => updateSetting('showOccupation', v)}
                label="Show occupation field"
                description="Show occupation dropdown during payout"
              />
              <TagListEditor
                value={settings.occupationList}
                onChange={(v) => updateSetting('occupationList', v)}
                label="Occupation options"
                description="Tenant-wide occupation options for the payout dropdown. For large lists, use Import to bulk upload from CSV/Excel (single column)."
                allowBulkImport={true}
              />
              <div className="py-3 border-t border-gray-100 mt-1">
                <div className="text-sm font-medium text-gray-900">Member card parse (JSON)</div>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                  Optional. PaySafeGo mobile parses magstripe / HID-wedge reads into <strong>member id</strong>. Use{' '}
                  <code className="text-xs bg-gray-100 px-1 rounded">CardInfo</code> with{' '}
                  <code className="text-xs bg-gray-100 px-1 rounded">CTN</code>,{' '}
                  <code className="text-xs bg-gray-100 px-1 rounded">CharSetRegularExpression</code>, and{' '}
                  <code className="text-xs bg-gray-100 px-1 rounded">Delimiters</code>.
                </p>
                <textarea
                  rows={10}
                  className="w-full font-mono text-xs border border-gray-300 rounded p-2"
                  spellCheck={false}
                  placeholder={'{\n  "CardInfo": {\n    "CTN": "1",\n    "CharSetRegularExpression": "^[0-9]+$",\n    "Delimiters": [{ "Start": ";", "End": "=" }]\n  }\n}'}
                  value={(() => {
                    const mcc = settings.memberCardParseConfig
                    if (mcc == null || mcc === '') return ''
                    return typeof mcc === 'string' ? mcc : JSON.stringify(mcc, null, 2)
                  })()}
                  onChange={(e) => {
                    const v = e.target.value
                    if (!v.trim()) {
                      updateSetting('memberCardParseConfig', null)
                      return
                    }
                    try {
                      updateSetting('memberCardParseConfig', JSON.parse(v))
                    } catch {
                      updateSetting('memberCardParseConfig', v)
                    }
                  }}
                />
              </div>
            </div>
          )}

          {activeSection === 'uar' && (
            <div className="space-y-4">
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={nilOnePerDay}
                  onChange={(e) => setNilOnePerDay(e.target.checked)}
                />
                <span>
                  One nil report per person per day
                  <span className="block text-xs text-gray-500">Off allows more than one no-incident report. A double tap within 60 seconds still returns the report just saved.</span>
                </span>
              </label>
              <UarCatalogEditor catalog={uarCatalog} onChange={setUarCatalog} />
              <div className="flex justify-end pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={saveUarCatalog}
                  disabled={savingUar || uarCatalog.length === 0}
                  className="px-4 py-2 bg-teal-700 text-white text-sm font-medium rounded-md hover:bg-teal-800 disabled:opacity-50"
                >
                  {savingUar ? 'Saving…' : 'Save UAR catalog'}
                </button>
              </div>
            </div>
          )}

          {activeSection === 'statements' && (
            <div className="space-y-1 divide-y divide-gray-100">
              <div className="py-2 pb-4">
                <div className="text-sm font-semibold text-gray-900">KYC consent statements</div>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">
                  Shown on mobile during KYC before identity verification. Empty content skips that statement. Leave title
                  blank to use the default labels Statement 1 and Statement 2.
                </p>
                <div className="space-y-3 rounded-lg border border-gray-200 p-4 bg-gray-50/50">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Statement 1 title</label>
                    <input
                      type="text"
                      value={settings.statement1Title || ''}
                      onChange={(e) => updateSetting('statement1Title', e.target.value)}
                      placeholder="Statement 1 (default when empty)"
                      className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <ParagraphContentEditor
                    value={settings.statement1Paragraphs}
                    onChange={(v) => updateSetting('statement1Paragraphs', v)}
                    label="Statement 1 content"
                    description="Separate paragraphs with a blank line (double line break)."
                    placeholder="First legal / consent block…"
                  />
                </div>
                <div className="space-y-3 rounded-lg border border-gray-200 p-4 bg-gray-50/50 mt-4">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Statement 2 title</label>
                    <input
                      type="text"
                      value={settings.statement2Title || ''}
                      onChange={(e) => updateSetting('statement2Title', e.target.value)}
                      placeholder="Statement 2 (default when empty)"
                      className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                  <ParagraphContentEditor
                    value={settings.statement2Paragraphs}
                    onChange={(v) => updateSetting('statement2Paragraphs', v)}
                    label="Statement 2 content"
                    description="Separate paragraphs with a blank line (double line break)."
                    placeholder="Second legal / consent block…"
                  />
                </div>
                <Toggle
                  checked={!!settings.printConsentOnReceipt}
                  onChange={(v) => updateSetting('printConsentOnReceipt', v)}
                  label="Print statement(s) on payout receipt"
                  description="When enabled, non-empty statements are appended to the printed payout report"
                />
              </div>

              <div className="py-2 pb-4 border-t border-gray-100">
                <div className="text-sm font-semibold text-gray-900">Patron declaration (signature step)</div>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">
                  Shown in the player signature sheet when enabled. Patron must check the box before drawing a signature.
                </p>
                <Toggle
                  checked={!!settings.patronDeclarationEnabled}
                  onChange={(v) => updateSetting('patronDeclarationEnabled', v)}
                  label="Enable patron declaration"
                />
                {settings.patronDeclarationEnabled && (
                  <>
                    <ParagraphContentEditor
                      value={settings.patronDeclarationParagraphs}
                      onChange={(v) => updateSetting('patronDeclarationParagraphs', v)}
                      label="Patron declaration content"
                      rows={4}
                      placeholder="I declare that the information I have provided is true and correct."
                    />
                    <div className="py-2">
                      <label className="text-sm font-medium text-gray-900">Patron declaration checkbox label</label>
                      <input
                        type="text"
                        value={settings.patronDeclarationCheckboxLabel || ''}
                        onChange={(e) => updateSetting('patronDeclarationCheckboxLabel', e.target.value)}
                        placeholder="I have read and agree to the above declaration"
                        className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="py-2 pb-4 border-t border-gray-100">
                <div className="text-sm font-semibold text-gray-900">Cashier declaration (signature step)</div>
                <p className="text-xs text-gray-500 mt-0.5 mb-3">
                  Shown in the cashier / staff signature sheet when enabled.
                </p>
                <Toggle
                  checked={!!settings.cashierDeclarationEnabled}
                  onChange={(v) => updateSetting('cashierDeclarationEnabled', v)}
                  label="Enable cashier declaration"
                />
                {settings.cashierDeclarationEnabled && (
                  <>
                    <ParagraphContentEditor
                      value={settings.cashierDeclarationParagraphs}
                      onChange={(v) => updateSetting('cashierDeclarationParagraphs', v)}
                      label="Cashier declaration content"
                      rows={4}
                      placeholder="I confirm that I have verified the patron's identity in accordance with venue procedures."
                    />
                    <div className="py-2">
                      <label className="text-sm font-medium text-gray-900">Cashier declaration checkbox label</label>
                      <input
                        type="text"
                        value={settings.cashierDeclarationCheckboxLabel || ''}
                        onChange={(e) => updateSetting('cashierDeclarationCheckboxLabel', e.target.value)}
                        placeholder="I confirm the above as cashier"
                        className="mt-1 w-full px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="py-2 border-t border-gray-100">
                <div className="text-sm font-semibold text-gray-900">Payout complete</div>
                <Toggle
                  checked={!!settings.payoutCompleteImageEnabled}
                  onChange={(v) => updateSetting('payoutCompleteImageEnabled', v)}
                  label="Optional image attachment on payout complete"
                  description="When enabled, staff may attach a non-mandatory photo at the payout complete step"
                />
              </div>
            </div>
          )}

          {activeSection === 'comments' && (
            <div className="space-y-1 divide-y divide-gray-100">
              <Toggle
                checked={settings.enableFloorComments}
                onChange={(v) => updateSetting('enableFloorComments', v)}
                label="Enable floor comments"
                description="Allow floor staff to add comments during payout"
              />
              <Toggle
                checked={settings.enableCoReviewComments}
                onChange={(v) => updateSetting('enableCoReviewComments', v)}
                label="Enable CO review comments"
                description="Allow compliance officer to add review comments"
              />
              <TagListEditor
                value={settings.floorCommentTags}
                onChange={(v) => updateSetting('floorCommentTags', v)}
                label="Floor comment tags"
                description="Predefined tag options for floor staff (shown as selectable chips)"
              />
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={fetchSettings}
          disabled={saving}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={saveSettings}
          disabled={saving}
          className="px-4 py-2 bg-teal-700 text-white rounded-md text-sm font-medium hover:bg-teal-800 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save configuration'}
        </button>
      </div>
    </div>
  )
}
